import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["preview"]
    static values = { productId: String, imageId: String, src: String }

    connect() {
        this.cropper = null
        this.rotation = 0
        this.Cropper = null
    }

    async loadCropper() {
        if (!this.Cropper) {
            const mod = await import("cropperjs")
            this.Cropper = mod.default
        }
        return this.Cropper
    }

    disconnect() {
        this.destroyCropper()
        this.removeModal()
    }

    async openEditor() {
        const objectUrl = await this.fetchImageAsObjectUrl()
        if (objectUrl) this.createModal(objectUrl)
    }

    async rotateRight() {
        const objectUrl = await this.fetchImageAsObjectUrl()
        if (objectUrl) this.createModal(objectUrl, () => {
            this.cropper?.rotate(90)
        })
    }

    async fetchImageAsObjectUrl() {
        const src = this.srcValue || this.previewTarget.src
        try {
            const response = await fetch(src)
            const blob = await response.blob()
            return URL.createObjectURL(blob)
        } catch (error) {
            console.error("Failed to load image:", error)
            return null
        }
    }

    async createModal(objectUrl, afterInit) {
        if (document.getElementById("image-editor-modal")) {
            this.removeModal()
        }

        this.objectUrl = objectUrl
        const src = objectUrl
        const modal = document.createElement("div")
        modal.id = "image-editor-modal"
        modal.className = "fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        modal.innerHTML = `
            <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
                <div class="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-slate-700">
                    <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Edit Image</h3>
                    <button type="button" id="editor-close" class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                </div>
                <div class="p-4 bg-gray-50 dark:bg-slate-900/50" style="max-height: 60vh;">
                    <img id="editor-image" src="${src}" style="max-width: 100%; display: block;">
                </div>
                <div class="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800">
                    <div class="flex items-center gap-1">
                        <button type="button" id="editor-rotate-left" class="p-2 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Rotate left">
                            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>
                        </button>
                        <button type="button" id="editor-rotate-right" class="p-2 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Rotate right">
                            <svg class="w-5 h-5 scale-x-[-1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>
                        </button>
                        <div class="w-px h-6 bg-gray-300 dark:bg-slate-600 mx-1"></div>
                        <button type="button" id="editor-flip-h" class="p-2 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Flip horizontal">
                            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 17h16m0 0l-4-4m4 4l-4 4M20 7H4m0 0l4-4M4 7l4 4"/></svg>
                        </button>
                        <button type="button" id="editor-flip-v" class="p-2 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Flip vertical">
                            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v16m0 0l-4-4m4 4l4-4M17 20V4m0 0l-4 4m4-4l4 4"/></svg>
                        </button>
                        <div class="w-px h-6 bg-gray-300 dark:bg-slate-600 mx-1"></div>
                        <button type="button" id="editor-reset" class="p-2 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Reset">
                            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                        </button>
                        <div class="w-px h-6 bg-gray-300 dark:bg-slate-600 mx-1"></div>
                        <button type="button" id="editor-remove-bg" class="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-1.5" title="Remove background">
                            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                            Remove BG
                        </button>
                    </div>
                    <div class="flex items-center gap-2">
                        <button type="button" id="editor-cancel" class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
                        <button type="button" id="editor-save" class="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 rounded-lg transition-colors">Save</button>
                    </div>
                </div>
            </div>
        `

        document.body.appendChild(modal)
        document.body.style.overflow = "hidden"

        const editorImage = document.getElementById("editor-image")
        this.scaleX = 1
        this.scaleY = 1

        const Cropper = await this.loadCropper()
        this.cropper = new Cropper(editorImage, {
            viewMode: 1,
            dragMode: "move",
            autoCropArea: 1,
            responsive: true,
            restore: false,
            checkCrossOrigin: false,
            checkOrientation: false,
            ready: () => {
                if (afterInit) afterInit()
            }
        })

        document.getElementById("editor-close").addEventListener("click", () => this.removeModal())
        document.getElementById("editor-cancel").addEventListener("click", () => this.removeModal())
        document.getElementById("editor-rotate-left").addEventListener("click", () => this.cropper?.rotate(-90))
        document.getElementById("editor-rotate-right").addEventListener("click", () => this.cropper?.rotate(90))
        document.getElementById("editor-flip-h").addEventListener("click", () => {
            this.scaleX = -this.scaleX
            this.cropper?.scaleX(this.scaleX)
        })
        document.getElementById("editor-flip-v").addEventListener("click", () => {
            this.scaleY = -this.scaleY
            this.cropper?.scaleY(this.scaleY)
        })
        document.getElementById("editor-reset").addEventListener("click", () => {
            this.scaleX = 1
            this.scaleY = 1
            this.cropper?.reset()
        })
        document.getElementById("editor-remove-bg").addEventListener("click", () => this.removeBg())
        document.getElementById("editor-save").addEventListener("click", () => this.saveEdit())

        modal.addEventListener("click", (e) => {
            if (e.target === modal) this.removeModal()
        })
    }

    async saveEdit() {
        if (!this.cropper) return

        const saveBtn = document.getElementById("editor-save")
        saveBtn.textContent = "Saving..."
        saveBtn.disabled = true

        const canvas = this.cropper.getCroppedCanvas({
            maxWidth: 2000,
            maxHeight: 2000,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: "high"
        })

        canvas.toBlob(async (blob) => {
            const formData = new FormData()
            formData.append("image", blob, "edited-image.jpg")

            const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
            const url = `/products/${this.productIdValue}/images/${this.imageIdValue}`

            try {
                const response = await fetch(url, {
                    method: "PATCH",
                    headers: {
                        "X-CSRF-Token": csrfToken,
                        "Accept": "application/json"
                    },
                    body: formData
                })

                if (response.ok) {
                    const data = await response.json()
                    this.previewTarget.src = data.url
                    this.removeModal()
                    // Reload to get fresh turbo-compatible DOM
                    window.location.reload()
                }
            } catch (error) {
                console.error("Save failed:", error)
                saveBtn.textContent = "Save"
                saveBtn.disabled = false
            }
        }, "image/jpeg", 0.92)
    }

    async removeBg() {
        const btn = document.getElementById("editor-remove-bg")
        const originalHtml = btn.innerHTML
        btn.innerHTML = `<svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Removing BG...`
        btn.disabled = true

        try {
            const canvas = this.cropper.getCroppedCanvas({
                maxWidth: 2000,
                maxHeight: 2000
            })

            const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"))
            const { removeBackground } = await import("@imgly/background-removal")
            const resultBlob = await removeBackground(blob)
            const newSrc = URL.createObjectURL(resultBlob)
            this.cropper.replace(newSrc)
        } catch (error) {
            console.error("Background removal failed:", error)
        } finally {
            btn.innerHTML = originalHtml
            btn.disabled = false
        }
    }

    destroyCropper() {
        if (this.cropper) {
            this.cropper.destroy()
            this.cropper = null
        }
    }

    removeModal() {
        this.destroyCropper()
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl)
            this.objectUrl = null
        }
        const modal = document.getElementById("image-editor-modal")
        if (modal) {
            modal.remove()
            document.body.style.overflow = ""
        }
    }
}
